import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import SearchBar from './components/SearchBar';
import CategoryCard from './components/CategoryCard';
import PopularArticles from './components/PopularArticles';
import QuickActions from './components/QuickActions';
import TicketSubmissionModal from './components/TicketSubmissionModal';
import LiveChatWidget from './components/LiveChatWidget';
import { useAuth } from '../../contexts/AuthContext';

const HelpCenterHub = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showLiveChat, setShowLiveChat] = useState(false);
  const [trendingTopics] = useState([
    'Creating Your First Form',
    'Conditional Logic',
    'Stripe Payments',
    'Multi-Step Forms',
    'Embedding Forms',
  ]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/authentication');
    }
  }, [user, authLoading, navigate]);

  const categories = [
    { id: 1, title: 'Knowledge Base',     description: 'Comprehensive guides and documentation',  icon: 'BookOpen',    articleCount: 19, color: 'text-blue-500',   bgColor: 'bg-blue-500/10',   path: '/knowledge-base-articles' },
    { id: 2, title: 'FAQ',                description: '16 frequently asked questions answered',   icon: 'HelpCircle',  articleCount: 16, color: 'text-green-500',  bgColor: 'bg-green-500/10',  path: '/support-ticket-system' },
    { id: 3, title: 'Form Building',      description: 'Fields, layouts, and conditional logic',   icon: 'Layout',      articleCount: 7,  color: 'text-orange-500', bgColor: 'bg-orange-500/10', path: '/knowledge-base-articles' },
    { id: 4, title: 'Integrations',       description: 'Stripe, webhooks, email, and embedding',  icon: 'Plug',        articleCount: 4,  color: 'text-cyan-500',   bgColor: 'bg-cyan-500/10',   path: '/knowledge-base-articles' },
    { id: 5, title: 'Analytics',          description: 'Metrics, responses, and CSV export',       icon: 'BarChart3',   articleCount: 2,  color: 'text-purple-500', bgColor: 'bg-purple-500/10', path: '/knowledge-base-articles' },
    { id: 6, title: 'Account & Billing',  description: 'Plans, limits, and account settings',      icon: 'CreditCard',  articleCount: 2,  color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', path: '/knowledge-base-articles' },
    { id: 7, title: 'Troubleshooting',    description: 'Common issues and how to fix them',        icon: 'AlertCircle', articleCount: 1,  color: 'text-red-500',    bgColor: 'bg-red-500/10',    path: '/knowledge-base-articles' },
    { id: 8, title: 'Submit a Ticket',    description: 'Can\'t find the answer? Contact us',       icon: 'Mail',        articleCount: 0,  color: 'text-pink-500',   bgColor: 'bg-pink-500/10',   path: '/support-ticket-system' },
  ];

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query?.trim()) {
      navigate(`/knowledge-base-articles?search=${encodeURIComponent(query)}`);
    }
  };

  const handleCategoryClick = (category) => {
    if (category?.path) {
      navigate(category?.path);
    }
  };

  if (authLoading) {
    return (
      <NavigationBar stats={{}}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </NavigationBar>
    );
  }

  return (
    <NavigationBar stats={{}}>
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                How can we help you?
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Search our knowledge base, browse articles, or get in touch with our support team
              </p>
            </div>

            {/* Search Bar */}
            <SearchBar
              searchQuery={searchQuery}
              onSearch={handleSearch}
              trendingTopics={trendingTopics}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Quick Actions */}
          <QuickActions
            onSubmitTicket={() => setShowTicketModal(true)}
            onStartChat={() => setShowLiveChat(true)}
          />

          {/* Categories Grid */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-semibold text-foreground">
                Browse by Category
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories?.map((category) => (
                <CategoryCard
                  key={category?.id}
                  category={category}
                  onClick={() => handleCategoryClick(category)}
                />
              ))}
            </div>
          </div>

          {/* Popular Articles */}
          <PopularArticles />

          {/* Contact Support Section */}
          <div className="mt-12 bg-card border border-border rounded-lg p-8 text-center">
            <div className="max-w-2xl mx-auto">
              <Icon name="Headphones" size={48} className="text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-heading font-semibold text-foreground mb-3">
                Still need help?
              </h3>
              <p className="text-muted-foreground mb-6">
                Can't find what you're looking for? Our support team is here to help you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Mail"
                  iconPosition="left"
                  onClick={() => setShowTicketModal(true)}
                >
                  Submit a Ticket
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="MessageCircle"
                  iconPosition="left"
                  onClick={() => setShowLiveChat(true)}
                >
                  Start Live Chat
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showTicketModal && (
          <TicketSubmissionModal onClose={() => setShowTicketModal(false)} />
        )}

        {/* Live Chat Widget */}
        {showLiveChat && (
          <LiveChatWidget onClose={() => setShowLiveChat(false)} />
        )}
      </div>
    </NavigationBar>
  );
};

export default HelpCenterHub;